function initializeRheidonCartRecommendations(element) {
  if (element.dataset.rheidonCartInitialized === "true") return;

  const track = element.querySelector("[data-rheidon-cart-track]");
  const previousButton = element.querySelector("[data-rheidon-cart-previous]");
  const nextButton = element.querySelector("[data-rheidon-cart-next]");
  if (!track || !previousButton || !nextButton) return;

  const updateControls = () => {
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    previousButton.disabled = track.scrollLeft <= 1;
    nextButton.disabled = track.scrollLeft >= maxScrollLeft - 1;
  };

  const scrollByCard = (direction) => {
    const firstCard = track.querySelector(
      ".rheidon-cart-recommendations__card"
    );
    if (!firstCard) return;

    const trackStyles = getComputedStyle(track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    track.scrollBy({
      left: direction * (firstCard.getBoundingClientRect().width + gap),
      behavior: "smooth",
    });
  };

  previousButton.addEventListener("click", () => scrollByCard(-1));
  nextButton.addEventListener("click", () => scrollByCard(1));
  track.addEventListener("scroll", updateControls, { passive: true });

  if ("ResizeObserver" in window) {
    element.rheidonResizeObserver = new ResizeObserver(updateControls);
    element.rheidonResizeObserver.observe(track);
  }

  element.dataset.rheidonCartInitialized = "true";
  requestAnimationFrame(updateControls);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (event) => event.code === "Escape" && this.close()
    );
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    this.querySelector("#CartDrawer-Overlay")?.addEventListener(
      "click",
      this.close.bind(this)
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() !== "SPACE") return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.querySelectorAll("rheidon-cart-recommendations").forEach(
      initializeRheidonCartRecommendations
    );

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    requestAnimationFrame(() => {
      this.classList.add("animate", "active");
    });

    const drawerDialog = this.querySelector(".drawer__inner");
    const focusElement = this.querySelector(".drawer__close") || drawerDialog;
    const trapDrawerFocus = () => {
      if (this.classList.contains("active") && drawerDialog) {
        trapFocus(drawerDialog, focusElement);
      }
    };

    drawerDialog?.addEventListener("transitionend", trapDrawerFocus, {
      once: true,
    });
    window.setTimeout(trapDrawerFocus, 400);
    document.body.classList.add("overflow-hidden");
  }

  close() {
    // Recommendation forms are replaced after an Ajax cart update, so their
    // saved trigger can become detached before the shopper closes the drawer.
    const focusReturnTarget = this.activeElement?.isConnected
      ? this.activeElement
      : document.querySelector("#cart-icon-bubble");

    this.classList.remove("active");
    removeTrapFocus(focusReturnTarget);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const parsedDrawer = this.getSectionDOM(
      parsedState.sections["cart-drawer"],
      "cart-drawer"
    );
    this.classList.toggle(
      "is-empty",
      Boolean(parsedDrawer?.classList.contains("is-empty"))
    );
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    window.setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}

class RheidonCartRecommendations extends HTMLElement {
  connectedCallback() {
    initializeRheidonCartRecommendations(this);
  }

  disconnectedCallback() {
    this.rheidonResizeObserver?.disconnect();
  }
}

if (!customElements.get("rheidon-cart-recommendations")) {
  customElements.define(
    "rheidon-cart-recommendations",
    RheidonCartRecommendations
  );
}
